import { create } from 'zustand';
import { getDefaultForm, getHydratedForm, useSettingsStore } from './useSettingsStore.js';
import { getCustomerOrderIds } from '../utils/orderUtils.js';
import { filterOffersForStock, isOutOfStock } from '../utils/stockUtils.js';

function bakerHeaders() {
  const { bakerPin, sessionId } = useSettingsStore.getState();
  const headers = {};
  if (bakerPin) {
    headers['x-baker-pin'] = bakerPin;
  }
  if (sessionId) {
    headers['x-session-id'] = sessionId;
  }
  return headers;
}

async function fetchAuthStatus() {
  const response = await fetch('/api/auth/status', { headers: bakerHeaders() });
  return { response, data: await response.json() };
}

async function fetchSessionId() {
  const response = await fetch('/api/auth/session-id', { headers: bakerHeaders() });
  return { response, data: await response.json() };
}

function summarizeCredential(configured, valid) {
  if (configured !== true) return 'unconfigured';
  if (valid === true) return 'valid';
  if (valid === false) return 'invalid';
  return 'unvalidated';
}

function storeOwnerToken(orderId, ownerToken) {
  useSettingsStore.setState((state) => ({
    ownerTokens: { ...state.ownerTokens, [orderId]: ownerToken },
  }));
}

function dropOwnerToken(orderId) {
  useSettingsStore.setState((state) => {
    const { [orderId]: _removed, ...rest } = state.ownerTokens || {};
    return { ownerTokens: rest };
  });
}

const LUCKY_OFFER_NAME = 'Brave Heart';

function sampleUnique(items, count) {
  const pool = [...items];
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}

export function rollLuckyToppings(config) {
  // The base offer and the category pool skip out-of-stock toppings so the
  // Lucky roll never contains a topping that is no longer available.
  const availableOffers = filterOffersForStock(config?.offers, config);
  const base = Array.isArray(availableOffers[0]?.toppings) ? availableOffers[0].toppings : [];
  const pool = [...new Set(Object.values(config?.categories || {}).flat())];
  const candidates = pool.filter((topping) => !base.includes(topping) && !isOutOfStock(config, topping));
  const total = 6 + Math.floor(Math.random() * 3);
  const extras = Math.max(0, total - base.length);
  return [...base, ...sampleUnique(candidates, extras)];
}

function appendLuckyOffer(config) {
  if (!config || !Array.isArray(config.offers)) {
    return config;
  }
  if (config.offers.some((offer) => offer.random)) {
    return config;
  }
  return {
    ...config,
    offers: [
      ...config.offers,
      { name: LUCKY_OFFER_NAME, alias: 'Lucky', random: true, toppings: rollLuckyToppings(config) },
    ],
  };
}

let sseReconnectTimer = null;
let sseReconnectAttempts = 0;

export const useAppStore = create((set, get) => ({
  config: null,
  orders: [],
  now: Date.now(),
  statusMessage: '',
  errorMessage: '',
  joinSelection: [],
  showDoneOrders: false,
  editingOrderId: null,
  // Set by the baker toolbar so the topping picker opens in stock-edit mode.
  stockEditRequested: false,
  eventSource: null,
  sseConnected: false,
  pinConfigured: null,
  sessionConfigured: null,
  setNow: (now) => set({ now }),
  requestStockEdit: () => set({ stockEditRequested: true }),
  consumeStockEdit: () => set({ stockEditRequested: false }),
  setStatusMessage: (message) => set({ statusMessage: message }),
  setErrorMessage: (message) => set({ errorMessage: message }),
  clearMessages: () => set({ statusMessage: '', errorMessage: '' }),
  checkAuthStatus: async () => {
    try {
      const { response, data } = await fetchAuthStatus();
      if (!response.ok) {
        return { pin: 'error', session: 'error' };
      }
      const pin = data.bakerPin || {};
      const session = data.session || {};
      set({
        pinConfigured: pin.configured === true,
        sessionConfigured: session.configured === true,
      });
      useSettingsStore.setState({
        pinValidated: pin.valid === true,
        sessionValidated: session.valid === true,
      });

      // PIN-validated baker device without a stored session id: auto-load it
      // so the baker's share URL/QR carries ?sessionId=... for the customers.
      if (pin.valid === true && session.configured === true && !useSettingsStore.getState().sessionId) {
        await get().autoJoinSession();
      }

      return {
        pin: summarizeCredential(pin.configured, pin.valid),
        session: summarizeCredential(session.configured, session.valid),
      };
    } catch {
      // Server unreachable — fail open; the server still enforces credentials.
      return { pin: 'error', session: 'error' };
    }
  },
  // Baker-only: fetches the session id for a PIN-validated device. Fail-open:
  // skipped when the server config is unknown (retried on the next check).
  autoJoinSession: async () => {
    const { sessionConfigured } = get();
    const { sessionId } = useSettingsStore.getState();
    if (sessionConfigured !== true || sessionId) {
      return;
    }
    try {
      const { response, data } = await fetchSessionId();
      if (response.ok && data.sessionId) {
        useSettingsStore.setState({ sessionId: data.sessionId });
        await get().checkAuthStatus();
      }
    } catch {
      // Server unreachable — keep going; the server still enforces the session id.
    }
  },
  toggleJoinSelection: (orderId) =>
    set((state) => ({
      joinSelection: state.joinSelection.includes(orderId)
        ? state.joinSelection.filter((id) => id !== orderId)
        : [...state.joinSelection, orderId].slice(0, 2),
    })),
  toggleShowDoneOrders: () =>
    set((state) => ({ showDoneOrders: !state.showDoneOrders })),
  resetForm: () => {
    const { customerName } = useSettingsStore.getState().form;
    useSettingsStore.setState({
      form: { ...getDefaultForm(get().config), customerName },
    });
  },
  connectSse: () => {
    if (typeof window === 'undefined') {
      return;
    }

    const existing = get().eventSource;
    if (existing) {
      return;
    }

    const eventSource = new EventSource('/api/events');
    eventSource.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'orders') {
          set({ orders: payload.orders || [] });
        } else if (payload.type === 'config') {
          // The server only announces that the config changed; refetch to get
          // the new toppings/out-of-stock state (keeps the Lucky roll + form).
          get().loadConfig().catch(() => {});
        }
      } catch (error) {
        console.error('Failed to parse SSE payload', error);
      }
    };

    eventSource.onerror = () => {
      set({ sseConnected: false });
      if (sseReconnectTimer) {
        return;
      }
      // Native EventSource retries on its own, but a capped-backoff force
      // recreate covers environments where the native retry stalls.
      sseReconnectAttempts += 1;
      const delay = Math.min(1000 * 2 ** sseReconnectAttempts, 30_000);
      sseReconnectTimer = setTimeout(() => {
        sseReconnectTimer = null;
        get().disconnectSse();
        get().connectSse();
      }, delay);
    };

    eventSource.onopen = () => {
      set({ sseConnected: true });
      if (sseReconnectTimer) {
        clearTimeout(sseReconnectTimer);
        sseReconnectTimer = null;
      }
      const reconnecting = sseReconnectAttempts > 0;
      sseReconnectAttempts = 0;
      // After a lost connection the server snapshot covers orders, but the
      // config (toppings) is never pushed over SSE — refetch both explicitly.
      if (reconnecting) {
        Promise.all([get().loadConfig(), get().loadOrders()]).catch(() => {});
      }
    };

    set({ eventSource, sseConnected: true });
  },
  disconnectSse: () => {
    if (sseReconnectTimer) {
      clearTimeout(sseReconnectTimer);
      sseReconnectTimer = null;
    }
    sseReconnectAttempts = 0;
    const eventSource = get().eventSource;
    if (eventSource) {
      eventSource.close();
    }
    set({ eventSource: null, sseConnected: false });
  },
  loadConfig: async () => {
    const response = await fetch('/api/config');
    const data = await response.json();
    const config = appendLuckyOffer(data);
    const form = getHydratedForm(config, useSettingsStore.getState().form);
    set({ config });
    useSettingsStore.setState({ form });
    return config;
  },
  rerollLuckyOffer: () => {
    const { config } = get();
    if (!config || !Array.isArray(config.offers)) {
      return;
    }
    const index = config.offers.findIndex((offer) => offer.random);
    if (index === -1) {
      return;
    }
    const offers = [...config.offers];
    offers[index] = { ...offers[index], toppings: rollLuckyToppings(config) };
    set({ config: { ...config, offers } });
    const form = useSettingsStore.getState().form;
    if (form.offer === offers[index].name) {
      useSettingsStore.setState({ form: { ...form, toppings: [...offers[index].toppings] } });
    }
  },
  loadOrders: async () => {
    const response = await fetch('/api/orders');
    const data = await response.json();
    set({ orders: Array.isArray(data) ? data : [] });
    return data;
  },
  createOrder: async (order) => {
    const response = await fetch('/api/orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bakerHeaders() },
      body: JSON.stringify(order),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to create order.');
    }
    // Do NOT optimistically push to orders — SSE delivers it to avoid duplicates.
    const settings = useSettingsStore.getState();
    const orderIds = [...getCustomerOrderIds(settings.currentCustomer), data.id];
    const customer = { orderIds, customerName: data.customerName };
    useSettingsStore.setState({
      currentCustomer: customer,
      // Preserve customerName in form so it stays prefilled for next order.
      form: { ...settings.form, customerName: data.customerName },
    });
    storeOwnerToken(data.id, data.ownerToken);
    return data;
  },
  updateOrder: async (orderId, payload) => {
    const response = await fetch(`/api/orders/${orderId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...bakerHeaders() },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update order.');
    }
    if (Array.isArray(data)) {
      set({ orders: data, joinSelection: [] });
    } else if (data.orders) {
      set({ orders: data.orders });
    } else {
      set((state) => ({
        orders: state.orders.map((order) => (order.id === orderId ? data : order)),
      }));
    }
    return data;
  },
  // Customer-edit of an own queued order: PUT { toppings, customToppings, comment }
  // with the owner token; the server requires authCustomer + a valid owner token
  // (a baker credential passes too). editingOrderId is runtime-only state.
  startEditOrder: (orderId) => {
    set({ editingOrderId: orderId });
    const order = useAppStore.getState().orders.find((entry) => entry.id === orderId);
    const settings = useSettingsStore.getState();
    if (order) {
      useSettingsStore.setState({
        form: {
          ...settings.form,
          customerName: order.customerName,
          offer: order.offer || '',
          toppings: [...(order.toppings || [])],
          customToppings: Array.isArray(order.customToppings) ? order.customToppings.join(', ') : '',
          comment: order.comment || '',
          half: !!order.half,
          menuTab: 'personalized',
        },
      });
    }
  },
  cancelEditOrder: () => {
    set({ editingOrderId: null });
  },
  editOrder: async (orderId, order) => {
    const ownerToken = useSettingsStore.getState().ownerTokens?.[orderId];
    const headers = { 'Content-Type': 'application/json', ...bakerHeaders() };
    if (ownerToken) {
      headers['x-owner-token'] = ownerToken;
    }
    const response = await fetch(`/api/orders/${orderId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(order),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update order.');
    }
    if (Array.isArray(data)) {
      set({ orders: data });
    } else {
      set((state) => ({
        orders: state.orders.map((entry) => (entry.id === orderId ? data : entry)),
      }));
    }
    return data;
  },
  // Baker-only: replaces the whole out-of-stock list. The server pushes a
  // config SSE event; loadConfig refetches so the UI updates immediately.
  saveOutOfStock: async (toppings) => {
    const response = await fetch('/api/toppings/out-of-stock', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...bakerHeaders() },
      body: JSON.stringify({ toppings: Array.isArray(toppings) ? toppings : [] }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update toppings.');
    }
    get().loadConfig().catch(() => {});
    return data;
  },
  reorderOrders: async (orderIds) => {
    const response = await fetch('/api/orders/reorder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bakerHeaders() },
      body: JSON.stringify({ orderIds }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to reorder orders.');
    }
    set({ orders: data });
    return data;
  },
  joinOrders: async (orderIds) => {
    const response = await fetch('/api/orders/join', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bakerHeaders() },
      body: JSON.stringify({ orderIds }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to join orders.');
    }
    set({ orders: data });
    return data;
  },
  deleteOrder: async (orderId) => {
    const ownerToken = useSettingsStore.getState().ownerTokens?.[orderId];
    const headers = { ...bakerHeaders() };
    if (ownerToken) {
      headers['x-owner-token'] = ownerToken;
    }
    const response = await fetch(`/api/orders/${orderId}`, {
      method: 'DELETE',
      headers,
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete order.');
    }
    dropOwnerToken(orderId);
    set({ orders: Array.isArray(data) ? data : [], joinSelection: [] });
    const customer = useSettingsStore.getState().currentCustomer;
    if (Array.isArray(customer?.orderIds) && customer.orderIds.includes(orderId)) {
      useSettingsStore.setState({
        currentCustomer: { ...customer, orderIds: customer.orderIds.filter((id) => id !== orderId) },
      });
    }
    return data;
  },
  disconnectOrder: async (orderId) => {
    const response = await fetch('/api/orders/disconnect', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bakerHeaders() },
      body: JSON.stringify({ orderId }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to disconnect orders.');
    }
    set({ orders: Array.isArray(data) ? data : [], joinSelection: [] });
    return data;
  },
}));
